import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, BookOpen, Settings } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { usePet } from "@/hooks/usePet";
import { useTypedTranslation } from "@/i18n/useTypedTranslation";
import { localePath } from "@/i18n/localePath";
import { useAppTab, type AppTab } from "@/stores/appTab";

// Always 3 items — never changes regardless of pet mode
const NAV_ITEMS = [
  { key: "chat", icon: MessageCircle, labelKey: "mobile_nav.chat", path: "/app/chat" },
  { key: "journal", icon: BookOpen, labelKey: "mobile_nav.journal", path: "/app/journal" },
  { key: "settings", icon: Settings, labelKey: "mobile_nav.settings", path: "/app/settings" },
] as const;

// Pages where bottom nav should NOT show
const HIDDEN_PATHS = ["/", "/auth", "/app/onboarding", "/garden", "/app/pet", "/ai-pet-companion", "/pet-memorial", "/about", "/pricing", "/ai-kompanion-pitomec", "/memorial-pitomca", "/o-nas", "/ceny", "/companero-mascota-ia", "/memorial-mascota", "/sobre-nosotros", "/precios"];

// Map nav item keys to AppTab values
const KEY_TO_TAB: Record<string, AppTab> = {
  chat: "chat",
  journal: "journal",
  settings: "settings",
};

export const MobileBottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { pet } = usePet();
  const { t, i18n } = useTypedTranslation();
  const lp = (path: string) => localePath(path, i18n.language);
  const [activeTab, setTab] = useAppTab();

  // Don't show if not logged in or no pet
  if (!user || !pet) return null;

  // Don't show on public/landing pages
  const isHidden = HIDDEN_PATHS.some((p) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p)
  );
  if (isHidden) return null;

  // On chat page: show on all screen sizes (GlobalNavbar is hidden there).
  // React Router location stays at /app/chat even after replaceState, so this is stable.
  const isChat = location.pathname.includes("/app/chat");

  const isActive = (itemKey: string, path: string) => {
    if (isChat) return activeTab === (KEY_TO_TAB[itemKey] ?? "chat");
    return location.pathname === lp(path) || location.pathname === path;
  };

  const handleClick = (itemKey: string, path: string) => {
    if (isChat) {
      setTab(KEY_TO_TAB[itemKey] ?? "chat");
    } else {
      navigate(lp(path));
    }
  };

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-40 ${isChat ? "" : "sm:hidden"}`}
      style={{
        background: "rgba(255,248,240,0.9)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderTop: "1px solid rgba(255,200,150,0.12)",
      }}
    >
      <div className="flex items-center justify-around h-14 max-w-lg mx-auto">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.key, item.path);
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              onClick={() => handleClick(item.key, item.path)}
              className="flex flex-col items-center gap-0.5 py-1 px-3 min-w-0 relative"
              aria-label={t(item.labelKey as any)}
            >
              <Icon
                className="h-5 w-5 transition-colors"
                style={{
                  color: active
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))",
                }}
              />
              <span
                className="text-[10px] font-medium transition-colors"
                style={{
                  color: active
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))",
                  fontWeight: active ? 700 : 500,
                }}
              >
                {t(item.labelKey as any)}
              </span>
              {active && (
                <motion.div
                  layoutId="bottom-nav-indicator"
                  className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-primary"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
            </button>
          );
        })}
      </div>
      {/* Safe area padding for iOS */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
};
